/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import {
	Suspense,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';

import { css } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
// eslint-disable-next-line @atlaskit/editor/warn-no-restricted-imports
import type { EditorActions } from '@atlaskit/editor-core/actions';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/EditorContext';
import { WithEditorActions } from '@atlaskit/editor-core/WithEditorActions';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import type { MentionAgentSource } from '@atlassian/conversation-assistant-agent/services/mention-agents/types';
import {
	MAX_CHARACTERS_INPUT,
	useEditorPreset,
} from '@atlassian/conversation-assistant-content-renderer/controllers/editor';
import { useRovoMentionNodeDataProvider } from '@atlassian/conversation-assistant-content-renderer/utils/mention-node-data-provider';
import { RovoMentionNodeDataProviderConnector } from '@atlassian/conversation-assistant-content-renderer/utils/mention-node-data-provider-connector';
import { useRovoMentionProvider } from '@atlassian/conversation-assistant-content-renderer/utils/mention-provider';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import {
	type Agent,
	type FileUploadResponseItem,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useEditorPlaceholder';
import { useHighlightActionsEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useHighlightActionsEditorPlaceholder';
import {
	isDefaultRovoChat,
	ROVO_AGENT_UUID,
} from '@atlassian/conversation-assistant-utils/default-agent';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useConversationFiles } from '../../../../common/controllers/conversation-files';
import { useChatMentionResource } from '../../../../common/hooks/useChatMentionResource';
import type { SelectedAgentRef } from '../../../../common/services/rovo-chat-mention-resource/rovo-chat-mention-resource';
import { ChatInputActionButtons } from '../../../../common/ui/chat-input-action-buttons';
import { ComposableEditorWithSkills } from '../../../../common/ui/composable-editor';
import { InputContextListDisplay } from '../../../../common/ui/input-context-list-display';
import { type RovoApp } from '../../../../controllers/chat-mode-store/types';
import { useChatModeStoreActions } from '../../../../controllers/chat-mode-store/useChatModeStoreActions';
import chatInputMessages from '../../../chat-prompt-input/messages';
import { getRovoChatQuickInsertRank } from '../../../skills-editor-extension/getRovoChatQuickInsertRank';
import { isSpecialistAgentSelected } from '../../../skills-editor-extension/isSpecialistAgentSelected';
import { useSkillTagExtensionProvider } from '../../../skills-editor-extension/useSkillTagExtensionProvider';
import { AgentSelector } from '../../agent-selector/AgentSelector';
import type { PromptContentChangePayload } from '../../prompt-content-change-payload';
import { LabsLozenge, type LabsLozengeVariant } from './labs-lozenge';
import messages from './messages';
import { useInlineEditorProps } from './use-inline-editor-props';
import { hasRovoSkillInDoc } from './utils';

export type ArrowKeyPluginConfigMessage = {
	id: string;
	content: string | DocNode;
};

type PromptViewInternalProps = {
	productKey: string;
	siteId: string;
	orgId?: string;
	source: string;
	onSubmit: (params: {
		prompt: DocNode;
		deepResearchEnabled: boolean;
		thinkDeeperEnabled: boolean;
		fastModeEnabled: boolean;
		webSearchEnabled: boolean;
		appFilters?: RovoApp[];
		files?: FileUploadResponseItem[];
		addCurrentPageAsContextUserPreference?: boolean;
	}) => void;
	actions: EditorActions;
	/** `null` clears the selection and returns the prompt to default Rovo. */
	onAgentSelect: (agent: Agent | null) => void;
	isBrowseAgentsModalOpen: boolean;
	setIsBrowseAgentsModalOpen: (isBrowseAgentsModalOpen: boolean) => void;
	enableAgents?: boolean;
	/**
	 * The account ID of the current user. Required when `enableAgents` is true
	 * to power the @agent mention typeahead.
	 */
	currentUserId?: string;
	/**
	 * Agent source from `useMentionAgentSource()`. Provided by the lazy
	 * `PromptViewWithRelay` wrapper
	 */
	agentSource?: MentionAgentSource | null;
	/**
	 * Callback invoked when a user selects an agent from the @mention typeahead.
	 * Provided by the lazy `PromptViewWithRelay` wrapper.
	 */
	onAgentSelected?: (agent: SelectedAgentRef) => void;
	showMauiMediaPlaceholder?: boolean;
	/**
	 * Temporary prop for labs tag use with editor only
	 * @private
	 * @deprecated
	 */
	showLabsLozenge?: boolean;
	labsLozengeVariant?: LabsLozengeVariant;
	agent?: Agent | null;
	popupsMountPoint?: HTMLElement | null;
	useShortMessages?: boolean;
	compact?: boolean;
	onPromptContentChange?: (payload: PromptContentChangePayload) => void;
	defaultValue?: JSONDocNode;
	/**
	 * When frameless, hides the prompt chrome and trailing actions so an embedding surface
	 * can provide them. Pair with `showTrailingActions` to re-show those controls.
	 */
	appearance?: 'default' | 'frameless';
	/**
	 * Shows trailing add and submit controls while retaining the frameless surface.
	 * It has no effect with the default appearance.
	 */
	showTrailingActions?: boolean;
	/** Keeps prompt controls top-aligned while the editor grows. */
	keepTrailingActionsInline?: boolean;
	shouldRenderInPortal?: boolean;
	/**
	 * Renders an agent selector beside the prompt and removes the picker from the add menu.
	 * `RovoInlineChatPrompt` resolves this public layout decision before reaching PromptView.
	 */
	showInlineAgentSelector?: boolean;
	/**
	 * Resolved by RovoInlineChatPrompt so the Jira-only gate is not evaluated in shared prompt layout code.
	 */
	shouldCompensateEmptyPromptMargin?: boolean;
	/** Hides the inline agent name while preserving its selector affordance. */
	hideAgentName?: boolean;
	/** Overrides the locally computed placeholder before it reaches the editor preset. */
	placeholderText?: string;
	/** Receives imperative prompt controls. */
	controlsRef?: React.Ref<{ clear: () => void }>;
	/**
	 * Controls attachment placement:
	 * - `undefined`: render attachments inline, the default presentation.
	 * - `null`: a full-width rail is requested but its mount point is not ready.
	 * - `HTMLElement`: render attachments into that full-width rail.
	 */
	attachmentListMountPoint?: HTMLElement | null;
};

export type ChatPromptInputProps = Omit<PromptViewInternalProps, 'actions'>;
const STORE_KEY = 'inline_prompt_view';

/*
 * This editor inherits trailing-only spacing meant for a document body, which
 * in a one-line prompt pushes the text off the row's centre, away from the
 * agent selector and the trailing actions. Dropping the inherited margin and
 * matching the `space.100` the chromeless appearance pads under the last block
 * balances that space around the text instead.
 *
 * A negative margin here would instead make the content overflow the editor's
 * scroll container, which axe reports as a keyboard-inaccessible region.
 *
 * This rule ties the inherited one on specificity, hence `&&`. Compiled's `css`
 * because `@atlaskit/css` rejects descendant selectors.
 */
const resetEditorTrailingSpace = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&& .ak-editor-content-area .ProseMirror': {
		marginBlockEnd: token('space.0'),
		paddingBlockStart: token('space.100'),
	},
});

/** Carries `resetEditorTrailingSpace` without contributing a box of its own. */
const editorPassthrough = css({ display: 'contents' });

const styles = cssMap({
	stack: {
		width: '100%',
		marginBottom: token('space.negative.150'),
	},

	stackWithoutContexts: {
		display: 'contents',
	},

	stackWithContextsAndTrailingActions: {
		flex: 1,
		minWidth: 0,
		width: 'auto',
	},

	editorWrapper: {
		maxWidth: '100%',
		minWidth: '0px',
		display: 'flex',
		paddingTop: token('space.050'),
		marginBottom: token('space.negative.050'),
		// Prevent the actions from growing when inline with the editor
		flexGrow: 99999,
	},

	// The outer stack has no layout box when no contexts are attached, so its
	// negative margin cannot offset the editor's default block-end margin.
	editorWrapperWithoutContexts: {
		marginBottom: token('space.negative.200'),
	},

	// Both only offset the space removed above, and they shift this row against
	// the sibling action row it is centred with.
	rowFlushWithActions: {
		paddingTop: token('space.0'),
		marginBottom: token('space.0'),
	},

	editorWrapperCenterAligned: {
		alignItems: 'center',
	},

	editorWrapperTopAligned: {
		alignItems: 'flex-start',
	},

	// Lets the prompt shrink in narrow comment popups instead of crushing the
	// flex-shrink: 0 agent selector beside it (CCI-19349).
	editor: {
		flexGrow: 1,
		flexShrink: 1,
		minWidth: '0px',
	},

	// Remove padding from the editor content container in compact mode
	editorWrapperCompact: {
		// @ts-expect-error - nested .ak-editor-content-area selector not supported in cssMap strict types; required by ProseMirror editor styling
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.ak-editor-content-area': {
			paddingTop: token('space.0'),
			paddingRight: token('space.0'),
			paddingBottom: token('space.0'),
			paddingLeft: token('space.0'),
		},
	},

	editorWrapperFramelessWithInlineActions: {
		// @ts-expect-error - nested .placeholder-decoration selector not supported in cssMap strict types; required by ProseMirror editor styling
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.placeholder-decoration': {
			whiteSpace: 'nowrap',
		},
	},

	actions: {
		justifyContent: 'end',
		gap: token('space.100'),
		// Expand to full width when not on the same row as the editor
		flexGrow: 1,
	},

	actionsWithContextsAndTrailingActions: {
		flexGrow: 0,
		flexShrink: 0,
	},

	labsLozenge: {
		alignSelf: 'center',
	},
});

const PromptViewInternal = ({
	productKey,
	siteId,
	orgId,
	source,
	onSubmit,
	actions,
	onAgentSelect,
	isBrowseAgentsModalOpen,
	setIsBrowseAgentsModalOpen,
	enableAgents = false,
	currentUserId,
	agentSource,
	onAgentSelected,
	showLabsLozenge = false,
	labsLozengeVariant = 'default',
	showMauiMediaPlaceholder = false,
	agent,
	popupsMountPoint,
	useShortMessages = false,
	compact = false,
	shouldCompensateEmptyPromptMargin = false,
	onPromptContentChange,
	defaultValue,
	appearance = 'default',
	showTrailingActions = false,
	keepTrailingActionsInline = false,
	shouldRenderInPortal,
	showInlineAgentSelector = false,
	hideAgentName = false,
	placeholderText: placeholderTextOverride,
	controlsRef,
	attachmentListMountPoint,
}: PromptViewInternalProps) => {
	di(
		ComposableEditor,
		useEditorPlaceholder,
		useEditorPreset,
		useChatModeStoreActions,
		AtlassianRelayEnvironmentProvider,
	);

	const saveRef = useRef<() => void>();
	const [inputValue, setInputValue] = useState('');
	const [hasMentionOrInlineCard, setHasMentionOrInlineCard] = useState(false);

	const defaultHasSkill = useMemo(() => hasRovoSkillInDoc(defaultValue), [defaultValue]);
	const [hasSkill, setHasSkill] = useState(defaultHasSkill);
	useEffect(() => {
		if (defaultHasSkill) {
			setHasSkill(true);
		}
	}, [defaultHasSkill]);
	const submitButtonRef = useRef<HTMLButtonElement>(null);
	const { value: mentionResourceOverride } = useRovoMentionProvider();
	const { placeholder, clearPlaceholder } = useEditorPlaceholder();
	const { highlightActionsEditorPlaceholder, clearHighlightActionsEditorPlaceholder } =
		useHighlightActionsEditorPlaceholder();
	const { formatMessage } = useIntl();
	const selectedAgentId =
		enableAgents && agent
			? agent.is_default || isDefaultRovoChat({ agentId: agent.id, agentNamedId: agent.named_id })
				? ROVO_AGENT_UUID
				: agent.id
			: undefined;
	const hasSelectedSpecialistAgent = isSpecialistAgentSelected(
		selectedAgentId,
		enableAgents && !!agent,
	);
	const slashCommandExperimentEnabled = isExperimentEnabled('platform_editor_slash_command');
	const isConversationAgentic = slashCommandExperimentEnabled
		? hasSelectedSpecialistAgent
		: enableAgents && !!agent;
	const placeholderText =
		placeholderTextOverride ??
		(showMauiMediaPlaceholder ? formatMessage(messages.chatInputAssistiveLabel) : undefined);

	const assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'] = {
		siteId,
		experienceId: 'ai-mate',
		product: productKey,
		touchpointSource: source,
	};

	const { files, clearAttachments, anyFilesUploading } = useConversationFiles({
		assistanceServiceParams,
		key: STORE_KEY,
	});
	const hasAttachments = files.length > 0;
	const hasContextsAndTrailingActions =
		appearance === 'frameless' &&
		keepTrailingActionsInline &&
		showTrailingActions &&
		hasAttachments;
	const attachmentList = (
		<InputContextListDisplay
			attachtmentsStoreKey={STORE_KEY}
			layout={attachmentListMountPoint === undefined ? 'scroll' : 'full-width-scroll'}
		/>
	);
	const latestPromptContentRef = useRef<PromptContentChangePayload>({
		textLength: 0,
		isEmpty: true,
		hasMentionOrInlineCard: false,
	});
	const hasAttachmentsRef = useRef(hasAttachments);
	const onPromptContentChangeWithAttachments = useCallback(
		(payload: PromptContentChangePayload) => {
			latestPromptContentRef.current = payload;
			onPromptContentChange?.({ ...payload, hasAttachments });
		},
		[hasAttachments, onPromptContentChange],
	);

	useEffect(() => {
		if (hasAttachmentsRef.current === hasAttachments) {
			return;
		}

		hasAttachmentsRef.current = hasAttachments;
		onPromptContentChange?.({ ...latestPromptContentRef.current, hasAttachments });
	}, [hasAttachments, onPromptContentChange]);

	const { getChatMode, resetReasoningMode, setWebSearchEnabled } = useChatModeStoreActions();

	const mentionResource = useChatMentionResource({
		productKey,
		siteId,
		orgId,
		currentUserId,
		mentionResourceOverride,
		enableAgents,
		agentSource,
		onAgentSelected,
	});
	// In the platform_editor_rovo_editor_mention_node_avatar control this returns undefined, so no
	// provider is constructed and the editor preset remains unchanged.
	const mentionNodeDataProvider = useRovoMentionNodeDataProvider({
		mentionResource,
		agentSource,
	});

	const inputExceedsMaxLimit = inputValue.length > MAX_CHARACTERS_INPUT;
	const isEmpty = inputValue.trim().length === 0;

	const isSendDisabled =
		inputExceedsMaxLimit || anyFilesUploading || (isEmpty && !hasMentionOrInlineCard && !hasSkill);

	const handleSubmit = useCallback(async () => {
		const value: DocNode = await actions.getValue();

		if (isSendDisabled) {
			return;
		}

		const {
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			webSearchEnabled,
			appFilters,
		} = getChatMode(STORE_KEY);
		const validFiles = files.filter((file) => !file.error);

		onSubmit({
			prompt: value,
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			webSearchEnabled,
			appFilters: appFilters,
			files: validFiles,
			addCurrentPageAsContextUserPreference: true,
		});

		// Clean up
		actions.clear();
		clearAttachments(STORE_KEY);
		resetReasoningMode(STORE_KEY);
		setWebSearchEnabled(STORE_KEY, false);
	}, [
		actions,
		clearAttachments,
		files,
		getChatMode,
		isSendDisabled,
		onSubmit,
		resetReasoningMode,
		setWebSearchEnabled,
	]);

	useEffect(() => {
		return () => {
			// Reset attachments on unmount
			clearAttachments(STORE_KEY);
		};
	}, [clearAttachments]);

	saveRef.current = handleSubmit;

	const stableOnSave = useCallback(() => saveRef.current?.(), []);

	useImperativeHandle(
		controlsRef,
		() => ({
			clear: () => {
				actions.clear();
				clearAttachments(STORE_KEY);
				actions.focus();
			},
		}),
		[actions, clearAttachments],
	);

	// Enable People/Agents section split in the @mention typeahead
	const enableAgentSectioning =
		(expValEquals('platform_editor_agent_mentions', 'isEnabled', true) &&
			fg('platform_editor_agent_mentions_drop_one_fixes')) ||
		fg('platform_editor_agent_mention_ask_rovo_jira');
	const { editorApi, preset, getDefaultPlaceholder } = useEditorPreset({
		onSave: stableOnSave,
		arrowKeyPluginConfig: {
			userMessages: [],
		},
		productKey,
		siteId,
		useShortMessages,
		placeholderText,
		enableAgentSectioning,
		quickInsertOptions: slashCommandExperimentEnabled
			? { getItemRank: getRovoChatQuickInsertRank }
			: undefined,
		...(mentionNodeDataProvider ? { mentionNodeDataProvider } : {}),
	});

	useEffect(() => {
		if (!slashCommandExperimentEnabled || !editorApi?.placeholder) {
			return;
		}

		const activePlaceholderText =
			placeholderText ??
			(hasSelectedSpecialistAgent
				? formatMessage(chatInputMessages.specialistAssistiveLabel)
				: getDefaultPlaceholder());

		editorApi.core.actions.execute(
			editorApi.placeholder.commands.setPlaceholder(activePlaceholderText),
		);
	}, [
		slashCommandExperimentEnabled,
		editorApi,
		formatMessage,
		getDefaultPlaceholder,
		hasSelectedSpecialistAgent,
		placeholderText,
	]);

	const closeTypeAhead = useCallback(() => {
		editorApi?.typeAhead?.actions.close({ insertCurrentQueryAsRawText: false });
	}, [editorApi?.typeAhead?.actions]);

	const openMentionTypeAhead = useCallback(() => {
		closeTypeAhead();
		// Small delay to ensure the editor regains focus after closing the dropdown menu
		// This prevents the typeahead from immediately closing in Safari
		// Original root cause research: https://product-fabric.atlassian.net/browse/AFC-550
		requestAnimationFrame(() => {
			editorApi?.core.actions.focus();
			editorApi?.mention?.actions.openTypeAhead(INPUT_METHOD.QUICK_INSERT);
		});
	}, [closeTypeAhead, editorApi?.core.actions, editorApi?.mention?.actions]);

	const openLinkTypeAhead = useCallback(() => {
		closeTypeAhead();
		const command = editorApi?.hyperlink?.commands.showLinkToolbar(INPUT_METHOD.QUICK_INSERT);
		editorApi?.core.actions.execute(command);
	}, [closeTypeAhead, editorApi?.core.actions, editorApi?.hyperlink?.commands]);

	const openAddTypeAhead = useCallback(() => {
		closeTypeAhead();
		// Small delay to ensure the editor regains focus after closing the dropdown menu
		// This prevents the typeahead from immediately closing in Safari
		// Original root cause research: https://product-fabric.atlassian.net/browse/AFC-550
		requestAnimationFrame(() => {
			editorApi?.core.actions.focus();
			editorApi?.quickInsert?.actions.openTypeAhead(INPUT_METHOD.QUICK_INSERT);
		});
	}, [closeTypeAhead, editorApi?.core.actions, editorApi?.quickInsert?.actions]);

	useEffect(() => {
		if (!!placeholder && !!editorApi) {
			actions.clear();
			actions.focus();
			closeTypeAhead();
			actions.appendText(placeholder.prompt);

			editorApi?.core.actions.execute(({ tr }) => {
				const maxPos = tr.doc.content.size;
				const startPos = Math.min(placeholder.selectionStartIndex, maxPos);
				const endPos = Math.min(placeholder.selectionEndIndex, maxPos);

				const transaction = tr.setSelection(
					TextSelection.between(tr.doc.resolve(startPos), tr.doc.resolve(endPos)),
				);

				return transaction;
			});

			switch (placeholder.type) {
				case 'person':
					openMentionTypeAhead();
					break;
				case 'link':
					openLinkTypeAhead();
					break;
			}

			clearPlaceholder();
		}
	}, [
		editorApi,
		actions,
		clearPlaceholder,
		closeTypeAhead,
		editorApi?.core.actions,
		openLinkTypeAhead,
		openMentionTypeAhead,
		placeholder,
	]);

	useEffect(() => {
		if (!!highlightActionsEditorPlaceholder && !!editorApi) {
			actions.clear();
			actions.focus();
			closeTypeAhead();
			editorApi.core.actions.replaceDocument(highlightActionsEditorPlaceholder);
			editorApi?.core.actions.execute(({ tr }) => {
				const lastNode = tr.doc.lastChild;
				if (lastNode) {
					const lastNodePos = tr.doc.resolve(tr.doc.content.size - lastNode.nodeSize);
					const endPos = tr.doc.resolve(tr.doc.content.size);
					const transaction = tr.setSelection(TextSelection.between(lastNodePos, endPos));

					return transaction;
				}

				return null;
			});
			// This is a workaround to avoid the focus being lost in Jira (and Jira sub-products)
			if (productKey === 'jira' || productKey === 'jsm' || productKey === 'jpd') {
				requestAnimationFrame(() => {
					setTimeout(() => {
						actions.focus();
					}, 1500);
				});
			}
			clearHighlightActionsEditorPlaceholder();
		}
	}, [
		editorApi,
		actions,
		clearHighlightActionsEditorPlaceholder,
		closeTypeAhead,
		highlightActionsEditorPlaceholder,
		productKey,
	]);

	// Minimal extension provider that only registers the skill chip node renderer.
	// Passed to the Suspense fallback editor so skill chips in `defaultValue` ADF
	// render immediately without waiting for the skills Relay query to resolve.
	const skillTagFallbackProvider = useSkillTagExtensionProvider(siteId);

	const assistiveLabel = useMemo(
		() =>
			formatMessage(
				useShortMessages ? messages.chatInputAssistiveLabelShort : messages.chatInputAssistiveLabel,
			),
		[formatMessage, useShortMessages],
	);

	const editorProps = useInlineEditorProps({
		assistiveLabel,
		defaultValue,
		mentionResource,
		onPromptContentChange: onPromptContentChangeWithAttachments,
		popupsMountPoint,
		preset,
		setHasMentionOrInlineCard,
		setInputValue,
		stableOnSave,
	});

	return (
		<>
			{mentionNodeDataProvider && (
				<RovoMentionNodeDataProviderConnector
					mentionNodeDataProvider={mentionNodeDataProvider}
					mentionResource={mentionResource}
				/>
			)}
			<Stack
				testId="prompt-view-stack"
				xcss={cx(
					styles.stack,
					files.length === 0 && styles.stackWithoutContexts,
					hasContextsAndTrailingActions && styles.stackWithContextsAndTrailingActions,
				)}
			>
				<Box
					testId="prompt-view-editor-wrapper"
					xcss={cx(
						styles.editorWrapper,
						keepTrailingActionsInline
							? styles.editorWrapperTopAligned
							: styles.editorWrapperCenterAligned,
						files.length === 0 &&
							shouldCompensateEmptyPromptMargin &&
							styles.editorWrapperWithoutContexts,
						compact && styles.editorWrapperCompact,
						appearance === 'frameless' &&
							keepTrailingActionsInline &&
							styles.editorWrapperFramelessWithInlineActions,
						showInlineAgentSelector && styles.rowFlushWithActions,
					)}
				>
					{showInlineAgentSelector && (
						<AgentSelector
							agent={agent}
							onAgentSelect={onAgentSelect}
							assistanceServiceParams={assistanceServiceParams}
							siteId={siteId}
							shouldRenderInPortal={shouldRenderInPortal}
							compact={compact}
							isBrowseAgentsModalOpen={isBrowseAgentsModalOpen}
							setIsBrowseAgentsModalOpen={setIsBrowseAgentsModalOpen}
							hideAgentName={hideAgentName}
						/>
					)}
					<div css={[editorPassthrough, showInlineAgentSelector && resetEditorTrailingSpace]}>
						<Box xcss={styles.editor}>
							<ErrorBoundary
								fallbackRender={({ error }) => (
									<>
										<ExperienceFailure
											teamName="Rovo Chat"
											error={error}
											name={ExperienceName.LOAD_INLINE_EDITOR}
										/>
										<ExperienceFailure
											teamName="Rovo Chat"
											error={error}
											name={ExperienceName.LOAD_INLINE_CHAT_PROMPT_INPUT_SKILLS}
										/>
										{/* fallback to editor without skills quick-insert but with chip renderer */}
										<ComposableEditor
											extensionProviders={skillTagFallbackProvider}
											{...editorProps}
										/>
									</>
								)}
								onError={(error) => {
									logException({
										exception: error,
										name: ExperienceName.LOAD_INLINE_EDITOR,
										tags: {
											siteId: siteId,
										},
									});
								}}
							>
								<ExperienceStart name={ExperienceName.LOAD_INLINE_EDITOR} />
								<ExperienceStart name={ExperienceName.LOAD_INLINE_CHAT_PROMPT_INPUT_SKILLS} />
								<AtlassianRelayEnvironmentProvider>
									<UFOSegment name="inline-chat-prompt-input-skills">
										<Suspense
											fallback={
												<ComposableEditor
													extensionProviders={skillTagFallbackProvider}
													{...editorProps}
												/>
											}
										>
											<ComposableEditorWithSkills
												editorApi={editorApi}
												isSpecialistPromptInput={isConversationAgentic}
												selectedAgentId={selectedAgentId}
												promptHasSkill={hasSkill}
												enableSkills
												siteId={siteId}
												{...editorProps}
											/>
											<ExperienceSuccess
												name={ExperienceName.LOAD_INLINE_CHAT_PROMPT_INPUT_SKILLS}
											/>
										</Suspense>
									</UFOSegment>
								</AtlassianRelayEnvironmentProvider>
								<ExperienceSuccess name={ExperienceName.LOAD_INLINE_EDITOR} />
							</ErrorBoundary>
						</Box>
					</div>
				</Box>
				{attachmentListMountPoint
					? createPortal(attachmentList, attachmentListMountPoint)
					: attachmentListMountPoint === undefined
						? attachmentList
						: null}
			</Stack>
			<Inline
				testId="prompt-view-actions"
				xcss={cx(
					styles.actions,
					hasContextsAndTrailingActions && styles.actionsWithContextsAndTrailingActions,
				)}
			>
				{showLabsLozenge && (
					<Inline xcss={styles.labsLozenge}>
						<LabsLozenge variant={labsLozengeVariant} />
					</Inline>
				)}
				{(appearance !== 'frameless' || showTrailingActions) && (
					<>
						<Inline alignBlock="center" space="space.050">
							<ChatInputActionButtons
								siteId={siteId}
								attachmentsAndCurrentPageAsContextStoreKey={STORE_KEY}
								isDisabled={false}
								useCurrentPageContext={false}
								onLinkTypeAheadClick={openLinkTypeAhead}
								onMentionTypeAheadClick={openMentionTypeAhead}
								onAddTypeAheadClick={openAddTypeAhead}
								assistanceServiceParams={assistanceServiceParams}
								placement="bottom-end"
								onMenuOpen={closeTypeAhead}
								// Omitted in the `inline` layout so the picker isn't offered in two
								// places at once — the standalone left selector owns it there.
								onAgentSelect={enableAgents && !showInlineAgentSelector ? onAgentSelect : undefined}
								isBrowseAgentsModalOpen={isBrowseAgentsModalOpen}
								setIsBrowseAgentsModalOpen={setIsBrowseAgentsModalOpen}
								shouldRenderInPortal={shouldRenderInPortal}
							/>
						</Inline>

						<IconButton
							isTooltipDisabled={false}
							appearance={fg('rovo_chat_neutral_message_and_prompt_buttons') ? 'rovo' : 'primary'}
							shape={
								UNSAFE_expValNoExposure('platform_editor_ai_prompt_updates', 'isEnabled', false)
									? 'default'
									: 'circle'
							}
							ref={submitButtonRef}
							onClick={() => saveRef.current?.()}
							type="submit"
							isDisabled={isSendDisabled}
							interactionName="ai-mate.send-message-button"
							icon={(iconProps) => <ArrowUpIcon {...iconProps} label={''} />}
							label={formatMessage(messages.sendButtonLabel)}
						/>
					</>
				)}
			</Inline>
		</>
	);
};

export const PromptView = (props: ChatPromptInputProps): JSX.Element => {
	return (
		<EditorContext>
			<WithEditorActions
				render={(actions) => <PromptViewInternal {...props} actions={actions} />}
			/>
		</EditorContext>
	);
};
